/**
 * Barcode scanners.
 *
 * A USB or Bluetooth scanner pretends to be a keyboard: it types the barcode
 * very fast and presses Enter. We watch for that pattern anywhere on the page -
 * the cashier does not have to click into a box first, which is the single
 * biggest time saver at a busy till.
 *
 * A human typing is far slower than a scanner, so the speed test alone separates
 * them reliably without stealing normal typing.
 */
import { normalizeScan } from '@pos/shared';

/** A scanner types a character every 10-30ms; a person rarely beats 50ms. */
const MAX_GAP_MS = 60;
const MIN_LENGTH = 4;

export interface ScannerOptions {
  onScan: (code: string) => void;
  /** Ignore scans while a modal owns the keyboard, for example. */
  enabled?: () => boolean;
}

export function listenForScanner({ onScan, enabled }: ScannerOptions): () => void {
  let buffer = '';
  let lastAt = 0;

  const handler = (e: KeyboardEvent) => {
    if (enabled && !enabled()) return;

    const target = e.target as HTMLElement | null;
    const inTypingField =
      target &&
      (target.tagName === 'TEXTAREA' ||
        (target.tagName === 'INPUT' && !(target as HTMLInputElement).dataset.scanTarget));

    const now = Date.now();
    const gap = now - lastAt;
    lastAt = now;

    if (e.key === 'Enter') {
      const code = normalizeScan(buffer);
      buffer = '';
      // Only treat it as a scan if the characters arrived at machine speed.
      if (code.length >= MIN_LENGTH && gap < MAX_GAP_MS * 4) {
        // A scan into a normal text box still counts, but let the box keep it too.
        if (!inTypingField) e.preventDefault();
        onScan(code);
      }
      return;
    }

    if (e.key.length !== 1) return;
    if (gap > MAX_GAP_MS) buffer = '';
    buffer += e.key;
  };

  window.addEventListener('keydown', handler, true);
  return () => window.removeEventListener('keydown', handler, true);
}

/**
 * Camera scanning for phones and tablets. Uses the browser's own BarcodeDetector
 * where it exists (Android Chrome), and falls back to a small library elsewhere.
 */
export interface CameraScanner {
  stop: () => void;
}

type DetectorLike = { detect: (s: CanvasImageSource) => Promise<{ rawValue: string }[]> };

export async function startCameraScan(
  video: HTMLVideoElement,
  onScan: (code: string) => void,
): Promise<CameraScanner> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: 'environment', width: { ideal: 1280 } },
  });
  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  await video.play();

  let running = true;
  const stopStream = () => {
    running = false;
    stream.getTracks().forEach((t) => t.stop());
  };

  const formats = ['code_128', 'ean_13', 'ean_8', 'code_39', 'upc_a', 'upc_e', 'qr_code'];
  const Native = (window as unknown as { BarcodeDetector?: new (o: unknown) => DetectorLike }).BarcodeDetector;

  if (Native) {
    const detector = new Native({ formats });
    const tick = async () => {
      if (!running) return;
      try {
        const found = await detector.detect(video);
        if (found[0]?.rawValue) onScan(normalizeScan(found[0].rawValue));
      } catch {
        /* a dropped frame is nothing to worry about */
      }
      if (running) requestAnimationFrame(() => void tick());
    };
    void tick();
    return { stop: stopStream };
  }

  // Fallback: pull the library in only when it is actually needed.
  try {
    const zxing = await import('@zxing/browser');
    const reader = new zxing.BrowserMultiFormatReader();
    const controls = await reader.decodeFromVideoElement(video, (result) => {
      if (result) onScan(normalizeScan(result.getText()));
    });
    return {
      stop: () => {
        controls.stop();
        stopStream();
      },
    };
  } catch {
    stopStream();
    throw new Error('This browser cannot scan with the camera. Use a scanner, or type the number.');
  }
}

export function cameraAvailable(): boolean {
  return !!navigator.mediaDevices?.getUserMedia;
}
